import type { MaskitoOptions } from "@maskito/core";
import type { MaskitoNumberParams } from "@maskito/kit";
import { maskitoNumberOptionsGenerator } from "@maskito/kit";

import { createTrailingDecimalHandlers } from "./createTrailingDecimalHandlers";
import { createTrailingZerosHandlers } from "./createTrailingZerosHandlers";

/**
 * Creates Maskito options for number input with trailing decimal and zero support.
 *
 * When `minimumFractionDigits` is 0, Maskito rejects trailing decimals ("123.")
 * and strips trailing zeros ("123.0" → "123."). This extends the base options
 * with processors to allow natural decimal input during typing.
 */
export function createNumberMaskOptions(
  params: MaskitoNumberParams,
): MaskitoOptions {
  const baseOptions = maskitoNumberOptionsGenerator(params);
  const minimumFractionDigits = params.minimumFractionDigits ?? 0;

  // When minimumFractionDigits > 0, Maskito handles everything correctly
  if (minimumFractionDigits > 0) {
    return baseOptions;
  }

  // Only add custom processors when minimumFractionDigits is 0
  const trailingDecimalHandlers = createTrailingDecimalHandlers();
  const trailingZerosHandlers = createTrailingZerosHandlers(params);

  return {
    ...baseOptions,
    preprocessors: [
      trailingDecimalHandlers.preprocessor,
      trailingZerosHandlers.preprocessor,
      ...(baseOptions.preprocessors ?? []),
    ],
    postprocessors: [
      ...(baseOptions.postprocessors ?? []),
      trailingDecimalHandlers.postprocessor,
      trailingZerosHandlers.postprocessor,
    ],
    plugins: [...(baseOptions.plugins ?? []), trailingZerosHandlers.blurPlugin],
  };
}
